import React, { useEffect, useState, useRef, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUpRight } from '@phosphor-icons/react';
interface CaseStudy {
  title: string;
  slug: string;
  image: string;
  category: string;
  year: string;
  whyItMatters: string;
  metric: string;
  metricLabel: string;
}
const caseStudies: CaseStudy[] = [{
  title: 'Faculty Information System',
  slug: 'case-western',
  image: 'https://cdn.magicpatterns.com/uploads/r6QmYhNhTgdAoSYhnocL3u/CWRU_COMPV.png',
  category: 'AI EXPLORATION',
  year: '2024',
  whyItMatters: 'Led a 2-week, 16-interview research sprint and synthesized findings with affinity mapping, then designed AI CV parsing with 85%+ accuracy that cut faculty data entry from 30 minutes to seconds and secured an Amazon pilot.',
  metric: '30 min → seconds',
  metricLabel: 'Faculty CV entry time · 85%+ AI parsing accuracy · secured Amazon pilot'
}, {
  title: 'U.S. Bank Commercial Card',
  slug: 'us-bank',
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/cGTn1zWyBRLJq9z8XJ288f/usbthumb.png',
  category: 'FINANCIAL SERVICES',
  year: '2022-2024',
  whyItMatters: 'Redesigned the corporate One Card onboarding end to end, building 20 reusable components and cutting processing from 54 to 20 days, reaching 98% satisfaction, 96% digital adoption, and 20,000 hours saved annually.',
  metric: '98% satisfaction',
  metricLabel: '54→20 day onboarding · 96% digital adoption · 20,000 hours saved yearly'
}, {
  title: 'Unisys Stealth',
  slug: 'cyber-security',
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/odCcwZqxVnrHSvVy5xeJrh/unisys_thumb.png',
  category: 'DIGITAL PROTECTION',
  year: '2021-2022',
  whyItMatters: 'Redesigned Unisys Stealth\u2019s micro-segmentation and threat-analysis tools at platform scale, translating dense security data into clear visualizations and a calm, scannable workflow analysts can navigate confidently during a high-pressure shift.',
  metric: 'Complex → intuitive',
  metricLabel: 'Platform-wide redesign of micro-segmentation & threat-analysis tooling'
}, {
  title: 'Conversational AI',
  slug: 'ai-comparison',
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/d2sojXsLucASkJnUpLu1Ks/Gemini_Generated_Image_qq6ptsqq6ptsqq6p.png',
  category: 'AI COMPARISON',
  year: '2020-2021',
  whyItMatters: 'Benchmarked Watson, Dialogflow, Microsoft, and Amazon, then designed natural dialogue patterns and built working \u201cTrack Status\u201d and \u201cFee Lookup\u201d demos that drove the platform decision and reduced call-center volume.',
  metric: '4 platforms benchmarked',
  metricLabel: 'Drove the conversational-AI platform decision · reduced call volume'
}];
export function ParallaxCaseStudies() {
  const navigate = useNavigate();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [visibleItems, setVisibleItems] = useState<boolean[]>([false, false, false, false]);
  useEffect(() => {
    const currentSection = sectionRef.current;
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          caseStudies.forEach((_, index) => {
            setTimeout(() => {
              setVisibleItems(prev => {
                const newState = [...prev];
                newState[index] = true;
                return newState;
              });
            }, index * 120);
          });
        }
      });
    }, {
      threshold: 0.05,
      rootMargin: '0px 0px -40px 0px'
    });
    if (currentSection) {
      observer.observe(currentSection);
    }
    return () => {
      if (currentSection) {
        observer.unobserve(currentSection);
      }
    };
  }, []);
  const handleCardClick = (slug: string) => {
    sessionStorage.setItem('navigatedFromHome', 'true');
    sessionStorage.removeItem('cameFromChat');
    window.scrollTo({
      top: 0,
      behavior: 'instant'
    });
    navigate(`/case-study/${slug}`);
  };
  return <section ref={sectionRef} className="bg-white pt-24 sm:pt-32 md:pt-40 pb-24 sm:pb-32 md:pb-40 px-6 sm:px-12 md:px-16 lg:px-24 overflow-hidden" id="projects" data-id="element-1653">
      <div className="container mx-auto max-w-7xl" data-id="element-1654">
        {/* Unified Section Header */}
        <div className="mb-16 sm:mb-24" style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
        transition: 'opacity 0.8s ease-out, transform 0.8s ease-out'
      }} data-id="element-1655">
          <div className="text-xs tracking-[0.3em] uppercase text-slate-500 font-medium mb-4" style={{
          fontFamily: "'Ronzino', sans-serif"
        }} data-id="element-1656">
            SELECTED WORK
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-light text-slate-900 leading-[1.05] tracking-tight mb-6" style={{
          fontFamily: "'Gefika', sans-serif"
        }} data-id="element-1657">
            Case Studies
          </h2>
          <p className="text-base sm:text-lg text-slate-600 leading-relaxed mb-6 max-w-2xl" style={{
          fontFamily: 'system-ui, -apple-system, sans-serif'
        }} data-id="element-1658">
            My two latest case studies are under NDA review: AI projects in
            medical and pharmacy at Eli Lilly and AArete. I'll add them once
            cleared.
          </p>
          <div className="w-12 h-px bg-slate-900" data-id="element-1659"></div>
        </div>

        {/* Equal-sized 2x2 grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-6 items-stretch" data-id="element-1660">
          {caseStudies.map((study, index) => <div key={study.slug} className="h-full" style={{
          opacity: visibleItems[index] ? 1 : 0,
          transform: visibleItems[index] ? 'translateY(0)' : 'translateY(30px)',
          transition: `opacity 0.8s ease-out ${index * 0.08}s, transform 0.8s ease-out ${index * 0.08}s`
        }} data-id="element-1661">
              <button onClick={() => handleCardClick(study.slug)} aria-label={`View case study: ${study.title}`} className="group relative flex flex-col w-full h-full text-left bg-slate-50 border border-slate-200 rounded-md overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2 transition-all duration-300 hover:border-slate-300" data-id="element-1662">
                {/* Image fills tile */}
                <div className="relative w-full aspect-[4/3] sm:aspect-[16/10] overflow-hidden bg-slate-100" data-id="element-1663">
                  <img src={study.image} alt={study.title} className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]" style={{
                objectPosition: study.slug === 'us-bank' || study.slug === 'cyber-security' ? 'top' : 'center'
              }} data-id="element-1664" />
                  {/* Top-right View project hint (hover) */}
                  <div className="absolute top-4 right-4 sm:top-5 sm:right-5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1665">
                    <span className="inline-flex items-center gap-1.5 bg-slate-900 text-white text-[10px] tracking-[0.2em] uppercase font-medium px-3 py-1.5 rounded-full" style={{
                  fontFamily: "'Ronzino', sans-serif"
                }} data-id="element-1666">
                      View project
                      <ArrowUpRight className="w-3 h-3" weight="bold" data-id="element-1667" />
                    </span>
                  </div>
                </div>

                {/* Bottom content block */}
                <div className="p-6 sm:p-8 md:p-10" data-id="element-1668">
                  <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-medium mb-3" style={{
                fontFamily: "'Ronzino', sans-serif"
              }} data-id="element-1669">
                    {study.category}
                  </div>
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-light text-slate-900 leading-tight mb-5" style={{
                fontFamily: "'Gefika', sans-serif"
              }} data-id="element-1670">
                    {study.title}
                  </h3>

                  {/* Design Impact */}
                  <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-medium mb-2" style={{
                fontFamily: "'Ronzino', sans-serif"
              }} data-id="element-1671">
                    Design Impact
                  </div>
                  <p className="text-sm sm:text-base text-slate-700 leading-relaxed mb-6" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-1672">
                    {study.whyItMatters}
                  </p>

                  {/* Impact callout */}
                  <div className="pt-5 border-t border-slate-200" data-id="element-1673">
                    <p className="text-xs sm:text-sm font-semibold text-slate-900 leading-snug" style={{
                  fontFamily: 'system-ui, -apple-system, sans-serif'
                }} data-id="element-1674">
                      {study.metricLabel}
                    </p>
                  </div>
                </div>
              </button>
            </div>)}

          {/* Amira — Coming Soon, full-width across both columns on desktop */}
          <div className="lg:col-span-2" style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
          transition: 'opacity 0.8s ease-out 0.6s, transform 0.8s ease-out 0.6s'
        }} data-id="element-1675">
            <div className="relative w-full text-left bg-slate-900 border border-white/15 rounded-md overflow-hidden" data-id="element-1676">
              <div className="grid grid-cols-1 md:grid-cols-2 items-stretch" data-id="element-1677">
                {/* Mockup side */}
                <div className="relative aspect-[4/3] md:aspect-auto bg-slate-900 overflow-hidden" data-id="element-1678">
                  <img src="https://cdn.magicpatterns.com/uploads/x4S7EPV4CN7QuEvhUbsnuF/mockuuups-macbook-pro-16-inch-mockup-on-a-textured-gray-surface-with-dramatic-lighting.jpg" alt="Amira AI Design Engineer OS shown on a MacBook Pro" className="absolute inset-0 w-full h-full object-cover" data-id="element-1679" />
                  {/* Coming soon badge */}
                  <div className="absolute top-4 left-4 sm:top-5 sm:left-5" data-id="element-1680">
                    <span className="inline-flex items-center gap-1.5 bg-slate-900 text-white text-[10px] tracking-[0.2em] uppercase font-medium px-3 py-1.5 rounded-full shadow-lg" style={{
                    fontFamily: "'Ronzino', sans-serif"
                  }} data-id="element-1681">
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-400" data-id="element-1682" />
                      Coming soon
                    </span>
                  </div>
                </div>

                {/* Content side */}
                <div className="p-6 sm:p-8 md:p-10 lg:p-12 flex flex-col justify-center" data-id="element-1683">
                  <div className="text-[10px] tracking-[0.25em] uppercase text-white/50 font-medium mb-3" style={{
                  fontFamily: "'Ronzino', sans-serif"
                }} data-id="element-1684">
                    EXPERIMENTAL / AGENTIC AI
                  </div>
                  <h3 className="text-2xl sm:text-3xl md:text-4xl font-light text-white leading-tight mb-5" style={{
                  fontFamily: "'Gefika', sans-serif"
                }} data-id="element-1685">
                    Amira
                  </h3>

                  <p className="text-sm sm:text-base text-white/70 leading-relaxed mb-6" data-id="element-1686">
                    An experimental enterprise AI agent platform exploring what
                    happens when design, automation, memory, and software
                    execution live inside one system. A custom Design Studio,
                    browser automation, persistent project memory, BYOK model
                    routing, and a human-gated self-improvement loop. Built to
                    push past single-purpose AI chatbots toward a true AI
                    collaborator.
                  </p>

                  {/* Capability tags */}
                  <div className="flex flex-wrap gap-2 mb-6" data-id="element-1687">
                    {['Next.js + Python', 'Browser automation', 'Persistent memory', 'Dream Engine', 'BYOK model routing'].map(tag => <span key={tag} className="text-[10px] tracking-[0.15em] uppercase text-white/60 border border-white/15 rounded-full px-3 py-1.5" style={{
                    fontFamily: "'Ronzino', sans-serif"
                  }} data-id="element-1688">
                        {tag}
                      </span>)}
                  </div>

                  {/* Authorship + access */}
                  <div className="flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-white/50 font-medium pt-5 border-t border-white/10" style={{
                  fontFamily: "'Ronzino', sans-serif"
                }} data-id="element-1689">
                    <span className="text-white/70" data-id="element-1690">Built by Mateo</span>
                    <span className="text-white/20" data-id="element-1691">&middot;</span>
                    <span data-id="element-1692">Live demos available with interview</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}